import { Component, ErrorInfo, ReactNode } from 'react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error?: Error | undefined;
  errorInfo?: ErrorInfo | undefined;
}

export class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error };
  }

  override   componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    // Always log basic error for production
    console.error('🔴 ErrorBoundary: Caught error -', error.message);
    
    // Detailed logging in development only
    if (import.meta.env.DEV) {
      console.error('🔴 ErrorBoundary: Caught error (detailed)', {
        error: error.message,
        stack: error.stack,
        componentStack: errorInfo.componentStack,
        timestamp: new Date().toISOString(),
        url: window.location.href
      });
    }

    // Special handling for AuthContext errors - attempt recovery
    const isContextError = error.message?.includes('useAuth must be used within an AuthProvider');
    if (isContextError) {
      const retryKey = 'errorBoundaryAuthRetry';
      const retryCount = parseInt(sessionStorage.getItem(retryKey) || '0');
      
      if (retryCount < 2) {
        sessionStorage.setItem(retryKey, (retryCount + 1).toString());
        console.warn(`🟡 ErrorBoundary: AuthContext error, attempting recovery (${retryCount + 1}/2)`);
        
        // Attempt recovery by resetting state and trying again
        setTimeout(() => {
          this.setState({ hasError: false, error: undefined, errorInfo: undefined });
        }, 500);
        return;
      } else {
        sessionStorage.removeItem(retryKey);
        console.error('🔴 ErrorBoundary: AuthContext error recovery failed, showing error boundary');
      }
    }

    this.setState({
      error,
      errorInfo
    });

    // Report to error tracking service if available
    if (window.gtag) {
      window.gtag('event', 'exception', {
        description: `ErrorBoundary: ${error.message}`,
        fatal: false
      });
    }
  }

  handleRefresh = () => {
    // Clear error state and retry counts
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    sessionStorage.removeItem('errorBoundaryAuthRetry');
    sessionStorage.removeItem('authContextRetry');
    window.location.reload();
  };

  handleGoHome = () => {
    // Clear error state and retry counts
    this.setState({ hasError: false, error: undefined, errorInfo: undefined });
    sessionStorage.removeItem('errorBoundaryAuthRetry');
    sessionStorage.removeItem('authContextRetry');
    window.location.href = '/dashboard';
  };

  override render() {
    if (this.state.hasError) {
      // Check if this is a context error
      const isContextError = this.state.error?.message?.includes('useAuth must be used within an AuthProvider');
      
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen flex items-center justify-center bg-background px-4">
          <div className="max-w-md w-full text-center">
            <div className="mb-8">
              <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <svg className="w-8 h-8 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                </svg>
              </div>
              <h1 className="text-2xl font-bold text-foreground mb-2">
                {isContextError ? 'Authentication Error' : 'Something went wrong'}
              </h1>
              <p className="text-muted-foreground mb-6">
                {isContextError 
                  ? 'There was an issue with authentication. This sometimes happens when navigating quickly.'
                  : 'An unexpected error occurred. We\'ve been notified and are working to fix it.'
                }
              </p>
            </div>

            <div className="space-y-3">
              <button
                onClick={this.handleRefresh}
                className="w-full bg-primary text-primary-foreground hover:bg-primary/90 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Refresh Page
              </button>
              <button
                onClick={this.handleGoHome}
                className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/80 px-6 py-3 rounded-lg font-medium transition-colors"
              >
                Go to Dashboard
              </button>
            </div>

            {import.meta.env.DEV && this.state.error && (
              <details className="mt-8 text-left">
                <summary className="text-sm text-muted-foreground cursor-pointer hover:text-foreground">
                  Technical Details (Development Only)
                </summary>
                <div className="mt-2 p-4 bg-muted rounded-lg text-xs font-mono">
                  <div className="mb-2">
                    <strong>Error:</strong> {this.state.error.message}
                  </div>
                  {this.state.error.stack && (
                    <div className="mb-2">
                      <strong>Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1 text-xs">{this.state.error.stack}</pre>
                    </div>
                  )}
                  {this.state.errorInfo?.componentStack && (
                    <div>
                      <strong>Component Stack:</strong>
                      <pre className="whitespace-pre-wrap mt-1 text-xs">{this.state.errorInfo.componentStack}</pre>
                    </div>
                  )}
                </div>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
} 